import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Text as RnText } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Text, TextSize, TextTone } from '../text'
import { Tooltip, TooltipContent } from '../tooltip'

const OPEN_DELAY_MS = 400

export const isClamped = (node: unknown): boolean => {
    const element = node as {
        scrollHeight?: number
        clientHeight?: number
        scrollWidth?: number
        clientWidth?: number
    } | null
    if (!element) {
        return false
    }
    const overflowsVertically =
        !!element.scrollHeight &&
        !!element.clientHeight &&
        element.scrollHeight > element.clientHeight
    const overflowsHorizontally =
        !!element.scrollWidth && !!element.clientWidth && element.scrollWidth > element.clientWidth
    return overflowsVertically || overflowsHorizontally
}

export interface TruncatedTextProps {
    children: ReactNode
    title?: string
    numberOfLines?: number
    size?: TextSize
    tone?: TextTone
    className?: string
    textClassName?: string
    testID?: string
}

export const TruncatedText: React.FC<TruncatedTextProps> = ({
    children,
    title,
    numberOfLines = 1,
    size = 'body',
    tone = 'default',
    className,
    textClassName,
    testID,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const textRef = useRef<React.ComponentRef<typeof RnText> | null>(null)
    const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    useEffect(() => () => clearTimeout(openTimer.current), [])

    const open = () => {
        if (!title || !isClamped(textRef.current)) {
            return
        }
        openTimer.current = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS)
    }

    const close = () => {
        clearTimeout(openTimer.current)
        setIsOpen(false)
    }

    return (
        <Tooltip
            isOpen={isOpen}
            onClose={close}
            // Box, not Pressable: a nested Pressable claims the press responder
            // and the row would stop reacting to clicks. Hence pointer events.
            trigger={({ ref, 'aria-describedby': describedBy }) => (
                <Box
                    ref={ref}
                    aria-describedby={describedBy}
                    onPointerEnter={open}
                    onPointerLeave={close}
                    className={twMerge('max-w-full overflow-hidden', className)}
                >
                    <Text
                        ref={textRef}
                        size={size}
                        tone={tone}
                        numberOfLines={numberOfLines}
                        className={textClassName}
                        testID={testID}
                    >
                        {children}
                    </Text>
                </Box>
            )}
        >
            <TooltipContent>
                <Text size='body' tone='default'>
                    {title}
                </Text>
            </TooltipContent>
        </Tooltip>
    )
}
